/* Purchase Orders — the document raised to a supplier *before* goods arrive.
 * Modelled on Grn.jsx: list + detail panel + a slide-in "New PO" drawer whose
 * PO details and products sit on one page. A GRN later receives against
 * the PO.
 *
 * PHASE 1 (this file) is a STANDALONE document: draft -> approved -> sent
 * (or cancelled). It is built so PHASE 2 (full lifecycle: receive-against-PO,
 * open/received qty reconciliation, auto-close) drops in additively —
 * see SUPPLY_API_SPEC.md §2A. Phase-2 hooks are marked `LIFECYCLE:` in
 * comments; none of them are wired yet. */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import supplyService from "../../Services/supplyService";
import outOfStockService, { last7DaysRange, toDateTimeRange } from "./Reports/outOfStockService";
import poExportService from "./Reports/poExportService";
import { buildPoSeedLines } from "./Reports/poSeed";
import { useTaskCenter } from "../Layout/TaskCenterContext";
import { getResolvedWarehouseCode, getWarehouseInfo } from "../../Services/warehouseContext";
import { cbFmtINR, cbFmtNum, cbStatusVariant, CB_PAYMENT_TERMS } from "./supplyData";
import { fmtDate, fmtTime, fmtDateTime } from "../../Utils/dateFormat";
import {
  Button,
  Pill,
  Input,
  Select,
  Table,
  Tr,
  Td,
  KV,
  SectionHeader,
  ToolbarBar,
  TabBar,
  Eyebrow,
  LoadingState,
  ErrorState,
  InlineAlert,
  DateTimeRangeFilter,
  IconDownload,
  IconPlus,
  IconX,
  IconCheck,
  IconChevRight,
  IconArrowRight,
  IconReceipt,
  IconBook,
} from "./supplyKit";
import { WarehouseSelect } from "./warehousePicker";
import { useWarehouse } from "../Layout/WarehouseContext";

/* Async CSV export polling (shared reports-tasks pipeline). */
const EXPORT_POLL_INTERVAL = 1500;
const EXPORT_POLL_MAX_ATTEMPTS = 40; // ~60s before timing out
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const poTh = {
  padding: "5px 8px",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  textAlign: "left",
  borderBottom: "1px solid var(--border-default)",
  whiteSpace: "nowrap",
};
const poTd = { padding: "6px 8px", borderBottom: "1px solid var(--border-subtle)", verticalAlign: "middle" };

const fieldInput = {
  width: "100%",
  height: 30,
  padding: "0 8px",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  background: "var(--bg-panel)",
  color: "var(--fg-default)",
  outline: "none",
};
const fieldMono = { ...fieldInput, fontFamily: "var(--font-mono)", textAlign: "right" };
const fieldLabel = {
  display: "block",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  marginBottom: 4,
};
const iconBtn = (color) => ({
  border: 0,
  background: "transparent",
  cursor: "pointer",
  color,
  padding: 4,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

/* ---------- helpers ---------- */
const num = (v) => Number(v) || 0;

/* Per-line price / tax math. MRP-exclusive: purchase price drives the
   ordered value, discount % comes off the gross, GST is added on top. */
const poLineCalc = (l) => {
  const qty = num(l.quantity ?? l.ordered);
  const pp = num(l.purchasePrice ?? l.purchase);
  const sp = num(l.salePrice ?? l.sellingPrice);
  const gross = qty * pp;
  const discPct = num(l.discountPct);
  const discAmt = (gross * discPct) / 100;
  const taxable = Math.max(0, gross - discAmt);
  const taxPct = num(l.taxPct);
  const tax = (taxable * taxPct) / 100;
  const margin = pp > 0 ? ((sp - pp) / pp) * 100 : 0;
  return { qty, pp, sp, gross, discPct, discAmt, taxable, taxPct, tax, amount: taxable + tax, margin };
};

const poTotals = (lines, header = {}) => {
  let qty = 0,
    gross = 0,
    taxable = 0,
    tax = 0,
    amount = 0;
  lines.forEach((l) => {
    const c = poLineCalc(l);
    qty += c.qty;
    gross += c.gross;
    taxable += c.taxable;
    tax += c.tax;
    amount += c.amount;
  });
  return { items: lines.length, qty, gross, taxable, tax, amount };
};

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const PurchaseOrder = () => {
  const location = useLocation();
  const initialMode = location.state?.mode; // 'new' | undefined

  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(initialMode === "new");
  const [editing, setEditing] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  /* The store in scope, chosen once in the topbar. */
  const { code: wh } = useWarehouse();
  const [pos, setPos] = useState([]);
  const [poLines, setPoLines] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  /* No warehouse in filter state — the store comes from the app-wide selection in
     the topbar and is merged in at query time, so it cannot go stale here. */
  const [filters, setFilters] = useState(() => ({
    q: "",
    supplierId: "",
  }));
  const setF = (key, val) => setFilters((f) => ({ ...f, [key]: val }));


  /* Static reference data. Suppliers belong to a warehouse, so only the ones
     serving the store in scope are fetched — for every role, since the store is now
     a single app-wide choice. Keyed on `wh` so switching store refetches. */
  useEffect(() => {
    let alive = true;
    Promise.all([
      supplyService.getSuppliers({
        warehouseId: wh || undefined,
      }),
      supplyService.getWarehouses(),
    ])
      .then(([s, w]) => {
        if (!alive) return;
        setSuppliers(s || []);
        setWarehouses(w || []);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load reference data.");
      });
    return () => {
      alive = false;
    };
  }, [reloadKey, wh]);

  /* The suppliers that serve the store currently in scope. Ones with no
     warehouseId are kept — rows created before the field became mandatory would
     otherwise vanish from every store's list. No warehouse filter (a head looking
     across stores) shows everything rather than nothing. */
  const listSuppliers = useMemo(() => {
    if (!wh) return suppliers;
    return suppliers.filter((s) => !s.warehouseId || String(s.warehouseId) === String(wh));
  }, [suppliers, wh]);

  /* Changing the warehouse can leave a supplier filter that store does not use —
     which reads as "no POs" when the truth is "no such supplier here". Clear it. */
  useEffect(() => {
    if (!filters.supplierId) return;
    if (listSuppliers.some((s) => String(s.id) === String(filters.supplierId))) return;
    setF("supplierId", "");
  }, [listSuppliers, filters.supplierId]);

  /* PO detail lines — fetched on row selection. */
  useEffect(() => {
    if (selected == null) {
      setPoLines([]);
      return undefined;
    }
    let alive = true;
    supplyService
      .getPurchaseOrderLines(selected)
      .then((pl) => alive && setPoLines(pl || []))
      .catch(() => alive && setPoLines([]));
    return () => {
      alive = false;
    };
  }, [selected]);

  /* PO list — re-fetches when server-side filters change. */
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params = {};
    if (filters.supplierId) params.supplierId = filters.supplierId;
    if (wh) params.warehouseId = wh;
    supplyService
      .getPurchaseOrders(params)
      .then((p) => {
        if (!alive) return;
        setPos(p || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load purchase orders.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey, wh, filters.supplierId]);

  const supById = (id) => suppliers.find((s) => String(s.id) === String(id));

  /* Text search is client-side. */
  const q = filters.q.trim().toLowerCase();
  const matchesText = (p) => {
    if (!q) return true;
    const hay = [String(p.id), p.supplierName, supById(p.supplier)?.name, p.wh]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  };
  const filtered = pos.filter(matchesText);

  const buckets = {
    all: filtered,
    draft: filtered.filter((p) => p.status === "draft"),
    open: filtered.filter((p) => p.status === "approved" || p.status === "sent"),
    cancelled: filtered.filter((p) => p.status === "cancelled"),
  };
  const rows = buckets[tab] || filtered;
  const selectedPo = filtered.find((p) => p.id === selected);

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Purchase Orders"
        sub="Orders raised to suppliers · a GRN later receives the goods against the PO"
        actions={
          <>
            <Button icon={<IconDownload size={14} />}>Export</Button>
            <Button variant="primary" icon={<IconPlus size={14} />} onClick={() => setShowNew(true)}>
              New PO
            </Button>
          </>
        }
      />

      <TabBar
        value={tab}
        onChange={(t) => {
          setTab(t);
          setSelected(null);
        }}
        tabs={[
          { id: "all", label: "All", count: buckets.all.length },
          { id: "draft", label: "Drafts", count: buckets.draft.length },
          { id: "open", label: "Approved / Sent", count: buckets.open.length, alert: "amber" },
          { id: "cancelled", label: "Cancelled", count: buckets.cancelled.length },
        ]}
      />

      <ToolbarBar>
        <Input
          placeholder="PO no / supplier…"
          style={{ width: 260, maxWidth: "100%" }}
          value={filters.q}
          onChange={(e) => setF("q", e.target.value)}
        />
        <Select value={filters.supplierId} onChange={(e) => setF("supplierId", e.target.value)}>
          <option value="">All suppliers</option>
          {/* Only the ones serving the store in scope — see listSuppliers. */}
          {listSuppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Button
          onClick={() =>
            setFilters({
              q: "",
              supplierId: "",
            })
          }
        >
          Reset
        </Button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {rows.length} PO{rows.length !== 1 ? "s" : ""}
        </div>
      </ToolbarBar>

      {loading ? (
        <LoadingState label="Loading purchase orders…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <div
          className={selectedPo ? "grid grid-cols-1 lg:grid-cols-[1fr_440px]" : "grid grid-cols-1"}
          style={{
            gap: 12,
            alignItems: "start",
          }}
        >
          <Table
            columns={[
              { label: "PO #" },
              { label: "Date & time" },
              { label: "Supplier" },
              { label: "Expected" },
              { label: "Wh" },
              { label: "Items", align: "right" },
              { label: "Units", align: "right" },
              { label: "Value", align: "right" },
              { label: "Status" },
              { label: "", width: 24 },
            ]}
          >
            {rows.map((p) => {
              const sup = supById(p.supplier);
              return (
                <Tr
                  key={p.id}
                  selected={selected === p.id}
                  onClick={() => setSelected(selected === p.id ? null : p.id)}
                >
                  <Td mono style={{ fontWeight: 600 }}>
                    {p.id}
                  </Td>
                  <Td mono title={p.createdAt ? `Created ${fmtDateTime(p.createdAt)}` : undefined}>
                    <div>{fmtDate(p.date)}</div>
                    {fmtTime(p.createdAt) && (
                      <div style={{ fontSize: 10, color: "var(--fg-muted)" }}>{fmtTime(p.createdAt)}</div>
                    )}
                  </Td>
                  <Td>{sup?.name || p.supplierName}</Td>
                  <Td mono style={{ color: "var(--fg-muted)" }}>
                    {p.expectedDate || "—"}
                  </Td>
                  <Td>
                    <Pill variant="neutral">{p.wh}</Pill>
                  </Td>
                  <Td mono right>
                    {p.items}
                  </Td>
                  <Td mono right>
                    {cbFmtNum(p.orderedUnits)}
                  </Td>
                  <Td mono right style={{ fontWeight: 600 }}>
                    {cbFmtINR(p.value)}
                  </Td>
                  <Td>
                    <Pill variant={cbStatusVariant(p.status)} dot>
                      {p.status.replace(/_/g, " ")}
                    </Pill>
                  </Td>
                  <Td>
                    <IconChevRight size={12} style={{ color: "var(--fg-muted)" }} />
                  </Td>
                </Tr>
              );
            })}
          </Table>

          {selectedPo && (
            <PoDetail
              po={selectedPo}
              supplier={supById(selectedPo.supplier)}
              lines={poLines}
              onClose={() => setSelected(null)}
              onChanged={() => {
                setReloadKey((k) => k + 1);
                setSelected(null);
              }}
              onEdit={() => {
                setEditing({ po: selectedPo, lines: poLines });
                setShowNew(true);
              }}
            />
          )}
        </div>
      )}

      {showNew && (
        <PoNewDrawer
          suppliers={suppliers}
          warehouses={warehouses}
          editing={editing}
          onClose={() => {
            setShowNew(false);
            setEditing(null);
          }}
          onSaved={() => {
            setReloadKey((k) => k + 1);
            setShowNew(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

/* ---------------- Detail panel ---------------- */
const PoDetail = ({ po, supplier, lines, onClose, onChanged, onEdit }) => {
  const navigate = useNavigate();
  const { runTask } = useTaskCenter();
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const totals = poTotals(lines);

  /* Async CSV export for this PO via the global task center (keeps running / shows
     progress in the topbar bell even if the user navigates away). */
  const startExport = () => {
    if (exporting) return;
    setExporting(true);
    const poNumber = po.id;
    runTask({
      title: `PO ${poNumber} · CSV`,
      run: async (onProgress) => {
        onProgress("Requesting…");
        const task = await poExportService.requestExport([poNumber]);
        if (!task?.taskId) throw new Error("Export did not return a task id.");
        const fileName = task.fileName || `po-${String(poNumber).replace(/\//g, "-")}.csv`;
        const doDownload = () => poExportService.download(task.taskId, fileName);

        let cur = task;
        for (let i = 0; cur.status === "PENDING" && i < EXPORT_POLL_MAX_ATTEMPTS; i++) {
          onProgress("Generating report…");
          await sleep(EXPORT_POLL_INTERVAL);
          cur = await poExportService.exportStatus(task.taskId);
        }
        if (cur.status === "FAILED") throw new Error(cur.message || "Export generation failed.");
        if (cur.status !== "READY") throw new Error("Export timed out. Please try again.");

        onProgress("Downloading…");
        await doDownload();
        return {
          detail: `Downloaded${cur.rowCount != null ? ` · ${cbFmtNum(cur.rowCount)} rows` : ""}`,
          action: { label: "Download again", run: doDownload },
        };
      },
    });
    toast.info("Generating PO export — track progress in notifications.");
    // brief guard against double-submit; the task itself lives in the task center
    setTimeout(() => setExporting(false), 1200);
  };

  const runWith = async (label, fn) => {
    setBusy(true);
    try {
      await fn();
      toast.success(`${label} — ${po.id}.`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || `Could not ${label.toLowerCase()}.`);
    } finally {
      setBusy(false);
    }
  };

  /* Lifecycle actions are keyed by the human PO number (list-row id = poNumber). */
  const poNumber = po.id;
  const handleApprove = () => runWith("Approved", () => supplyService.approvePurchaseOrder(poNumber));
  const handleSend = () => runWith("Sent to supplier", () => supplyService.sendPurchaseOrder(poNumber));
  const handleCancel = () => {
    const reason = window.prompt(`Cancel ${po.id}? Enter a reason:`);
    if (reason == null) return;
    runWith("Cancelled", () => supplyService.cancelPurchaseOrder(poNumber, reason || "NA"));
  };
  /* LIFECYCLE: in Phase 2 this carries the PO so the GRN drawer prefills its
     lines and the backend reconciles received qty back to the PO. For now it
     just opens a fresh GRN on the right screen — a harmless forward hook. */
  const handleReceive = () =>
    navigate("/grn", {
      state: { mode: "new", fromPO: { id: po.id, poId: po.poId, supplier: po.supplier, warehouseId: po.warehouseId } },
    });

  return (
    <aside
      className="min-w-0 lg:sticky lg:top-3 lg:max-h-[calc(100vh-120px)]"
      style={{
        border: "1px solid var(--border-default)",
        borderRadius: 6,
        background: "var(--bg-panel)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--border-default)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: "var(--fg-strong)" }}>
              {po.id}
            </span>
            <Pill variant={cbStatusVariant(po.status)} dot>
              {po.status.replace(/_/g, " ")}
            </Pill>
          </div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 4, lineHeight: 1.4 }}>
            {fmtDate(po.date)} · {supplier?.name || po.supplierName} · expected{" "}
            <span style={{ fontFamily: "var(--font-mono)" }}>{fmtDate(po.expectedDate) || "—"}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Button size="sm" icon={<IconDownload size={13} />} onClick={startExport} disabled={exporting}>
            {exporting ? "Exporting…" : "Export"}
          </Button>
          <button
            onClick={onClose}
            style={{
              width: 24,
              height: 24,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              color: "var(--fg-muted)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconX size={14} />
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <KV label="Warehouse" value={po.wh} mono />
          <KV label="Supplier GSTIN" value={supplier?.gstin} mono small />
          <KV label="Payment terms" value={po.paymentTerms || supplier?.terms || "—"} />
          <KV label="Created by" value={po.createdBy || "—"} mono small />
        </div>

        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <Eyebrow>Line items · {lines.length}</Eyebrow>
          </div>
          <div style={{ border: "1px solid var(--border-default)", borderRadius: 4, overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}>
              <thead>
                <tr style={{ background: "var(--bg-subtle)" }}>
                  <th style={poTh}>Medicine</th>
                  <th style={{ ...poTh, textAlign: "right", width: 50 }}>Qty</th>
                  <th style={{ ...poTh, textAlign: "right", width: 64 }}>Rate</th>
                  <th style={{ ...poTh, textAlign: "right", width: 72 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const c = poLineCalc(l);
                  return (
                    <tr key={i}>
                      <td style={poTd}>
                        <div style={{ fontWeight: 600, color: "var(--fg-strong)", fontSize: 11 }}>
                          {l.medicineName || l.medId}
                        </div>
                        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-muted)", marginTop: 2 }}>
                          {l.medId} · GST {num(l.taxPct)}%
                        </div>
                        {l.manufacturer && (
                          <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 1 }}>{l.manufacturer}</div>
                        )}
                      </td>
                      <td style={{ ...poTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                        {c.qty}
                        {num(l.free) > 0 && <span style={{ color: "var(--fg-muted)" }}> +{num(l.free)}</span>}
                      </td>
                      <td style={{ ...poTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>{cbFmtINR(c.pp)}</td>
                      <td style={{ ...poTd, textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 600 }}>
                        {cbFmtINR(c.amount)}
                      </td>
                    </tr>
                  );
                })}
                {lines.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ ...poTd, textAlign: "center", color: "var(--fg-muted)", padding: 16 }}>
                      No line items.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div
          style={{
            padding: "8px 10px",
            background: "var(--bg-subtle)",
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontSize: 12,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Goods value</span>
            <span style={{ fontFamily: "var(--font-mono)" }}>{cbFmtINR(totals.taxable || po.totalAmount)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", color: "var(--fg-muted)" }}>
            <span>GST</span>
            <span style={{ fontFamily: "var(--font-mono)" }}>{cbFmtINR(totals.tax || po.gstAmount)}</span>
          </div>
          <div style={{ height: 1, background: "var(--border-subtle)", margin: "2px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, color: "var(--fg-strong)" }}>
            <span>Order total</span>
            <span style={{ fontFamily: "var(--font-mono)" }}>{cbFmtINR(totals.amount || po.value)}</span>
          </div>
        </div>

        <div
          style={{
            padding: "10px 12px",
            background: "var(--ink-50)",
            borderRadius: 4,
            fontSize: 11,
            color: "var(--ink-700)",
            lineHeight: 1.5,
          }}
        >
          {po.status === "sent" ? (
            <>
              <strong>Sent to supplier.</strong> When the goods arrive, receive them with a GRN — that step creates the
              inventory lots and IN transactions.
            </>
          ) : po.status === "draft" ? (
            <>
              <strong>Draft.</strong> Approve the PO, then send it to the supplier. No stock changes until a GRN is
              received against it.
            </>
          ) : (
            <>This purchase order is approved and ready to send to the supplier.</>
          )}
        </div>
      </div>

      <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border-default)", display: "flex", gap: 8, flexWrap: "wrap" }}>
        {(po.status === "draft" || po.status === "approved") && (
          <Button style={{ flex: 1, justifyContent: "center" }} onClick={onEdit} disabled={busy}>
            Edit
          </Button>
        )}
        {(po.status === "draft" || po.status === "approved") && (
          <Button variant="danger" style={{ flex: 1, justifyContent: "center" }} onClick={handleCancel} disabled={busy}>
            Cancel
          </Button>
        )}
        {po.status === "draft" && (
          <Button
            variant="primary"
            style={{ flex: 1.4, justifyContent: "center" }}
            icon={<IconCheck size={14} />}
            onClick={handleApprove}
            disabled={busy}
          >
            {busy ? "Approving…" : "Approve"}
          </Button>
        )}
        {po.status === "approved" && (
          <Button
            variant="primary"
            style={{ flex: 1.4, justifyContent: "center" }}
            icon={<IconArrowRight size={14} />}
            onClick={handleSend}
            disabled={busy}
          >
            {busy ? "Sending…" : "Send to supplier"}
          </Button>
        )}
        {po.status === "sent" && (
          <Button
            variant="primary"
            style={{ flex: 1, justifyContent: "center" }}
            icon={<IconReceipt size={14} />}
            onClick={handleReceive}
          >
            Receive · create GRN
          </Button>
        )}
      </div>
    </aside>
  );
};

/* ============================================================
   New PO drawer — single-page form (PO details + Products)
   ============================================================ */
const blankHeader = (suppliers, warehouses) => ({
  /* Never defaulted to a list position — see the note in Grn.jsx's blankHeader.
     Only the logged-in user's resolved warehouse may prefill it. */
  /* Blank for a head — they order for any store and must choose it explicitly. */
  /* The store in scope — a PO is raised for the store being worked in, which is
     chosen once in the topbar (getResolvedWarehouseCode returns that selection). */
  store: getResolvedWarehouseCode() || "",
  supplier: suppliers[0]?.id || "",
  poDate: today(),
  expectedDate: addDays(today(), 7),
  paymentTerms: suppliers[0]?.terms || CB_PAYMENT_TERMS[1],
  remarks: "",
});

const headerFromPo = (po, warehouses, suppliers) => ({
  store: po.warehouseId || "",
  supplier: po.supplier || suppliers[0]?.id || "",
  poDate: po.date || today(),
  expectedDate: po.expectedDate || addDays(po.date || today(), 7),
  paymentTerms: po.paymentTerms || CB_PAYMENT_TERMS[1],
  remarks: po.note || "",
});

const linesFromDetail = (detailLines) =>
  (detailLines || []).map((l) => ({
    medId: l.medId || "",
    productCode: l.productCode || l.medId || "",
    productName: l.medicineName || "",
    manufacturer: l.manufacturer || "",
    quantity: l.ordered ?? "",
    free: l.free ?? l.freeQuantity ?? "",
    purchasePrice: l.purchase ?? "",
    mrp: l.mrp ?? "",
    salePrice: l.sellingPrice ?? "",
    discountPct: l.discountPct ?? "",
    taxPct: l.taxPct ?? "",
  }));

const blankEntry = () => ({
  medId: "",
  productCode: "",
  productName: "",
  manufacturer: "",
  quantity: "",
  free: "",
  purchasePrice: "",
  mrp: "",
  salePrice: "",
  discountPct: "",
  taxPct: "",
});

/* Labels + subtitles per creation source (used by the header + source cards). */
const PO_SOURCE_LABEL = {
  manual: "Manual entry · build the order line by line",
  "low-stock": "From low stock · top up batches running below a threshold",
  "out-of-stock": "From out-of-stock orders · reorder shortfalls from stuck orders",
};

const PoNewDrawer = ({ onClose, suppliers, warehouses, editing, onSaved }) => {
  const isEditing = Boolean(editing?.po?.id);

  /* Creation flow: a fresh PO opens on the "source" chooser; edit jumps straight
     to the details step. `source` decides whether the item "picker" step
     (low-stock / out-of-stock) sits between the chooser and the PO details. */
  const [source, setSource] = useState(isEditing ? "manual" : null);
  /* PO details and Products are one page ("form") — same single-page treatment as
     the GRN drawer. The source chooser and the item picker stay separate steps. */
  const [step, setStep] = useState(isEditing ? "form" : "source"); // source | picker | form

  const [header, setHeader] = useState(() =>
    isEditing ? headerFromPo(editing.po, warehouses, suppliers) : blankHeader(suppliers, warehouses)
  );
  const [lines, setLines] = useState(() => (isEditing ? linesFromDetail(editing.lines) : []));
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  const setH = (key, val) => setHeader((h) => ({ ...h, [key]: val }));

  /* Only the suppliers that serve the store being ordered for — a supplier belongs
     to one warehouse, so ordering from another store's supplier is not a thing.
     Mirrors scopedSuppliers in Grn.jsx.

     Suppliers with no warehouseId are kept: rows created before the field became
     mandatory would otherwise vanish from every store's list. `store` empty
     (nothing picked yet) shows everything rather than nothing, so the dropdown is
     never mysteriously empty. */
  const scopedSuppliers = useMemo(() => {
    if (!header.store) return suppliers;
    /* When editing, the PO's own supplier stays in the list even if it no longer
       belongs to this store — an existing order must not have its supplier
       rewritten because the master changed after the fact. */
    const keepId = isEditing ? String(editing?.po?.supplier ?? "") : "";
    return suppliers.filter(
      (s) =>
        !s.warehouseId ||
        String(s.warehouseId) === String(header.store) ||
        (keepId && String(s.id) === keepId)
    );
  }, [suppliers, header.store, isEditing, editing]);

  /* Sync defaults once async reference lists arrive (no-op in edit mode). A
     supplier already chosen but not served by the selected store is cleared here
     too, so switching store can't leave a mismatched supplier on the PO. */
  useEffect(() => {
    setHeader((h) => {
      const next = { ...h };
      const stillValid =
        next.supplier && scopedSuppliers.some((s) => String(s.id) === String(next.supplier));
      if (!stillValid) next.supplier = scopedSuppliers[0]?.id || "";
      /* Store is deliberately NOT defaulted from the list — picking warehouses[0]
         meant stock could be received into, or ordered for, a warehouse the
         operator never chose. It stays blank until they select one (or the
         logged-in user's warehouse resolved it in blankHeader). */
      return next;
    });
  }, [scopedSuppliers, warehouses]);

  const supplier = suppliers.find((s) => String(s.id) === String(header.supplier));
  const totals = useMemo(() => poTotals(lines, header), [lines, header]);

  const headerValid = header.supplier && header.store && header.poDate;
  const canSave = headerValid && lines.length > 0;

  /* Ordered wizard steps for the current mode/source — drives StepDots + Back. */
  const flow =
    isEditing || source === "manual"
      ? ["form"]
      : source === "low-stock" || source === "out-of-stock"
      ? ["picker", "form"]
      : ["source"];

  const canGoBack = step === "picker" || (step === "form" && !isEditing);
  const goBack = () => {
    const i = flow.indexOf(step);
    if (i > 0) setStep(flow[i - 1]);
    else setStep("source"); // form (manual) / picker -> back to the chooser
  };

  const pickSource = (src) => {
    setSource(src);
    setStep(src === "manual" ? "form" : "picker");
  };

  /* Picker hands back reorder lines + the warehouse they were pulled from. */
  const applyPicked = (seedLines, warehouseId) => {
    setLines(seedLines);
    if (warehouseId) setH("store", warehouseId);
    setStep("form");
  };

  /* Create -> POST /v1/purchase-orders (DRAFT or APPROVED). Edit -> PATCH.
     Nothing here touches inventory — a GRN does that, later, against the PO. */
  const handleSubmit = (status) => {
    setSaving(true);
    setSaveErr(null);
    /* Edits are keyed by the human PO number (list-row id = poNumber). */
    const poNumber = editing?.po?.id;
    /* Payment terms come from the supplier master (set at onboarding) rather than
       the form. Resolve at submit so a supplier chosen before the list loaded — or
       defaulted in by the sync effect — still posts that supplier's terms. */
    const payload = {
      header: { ...header, paymentTerms: supplier?.terms || header.paymentTerms },
      lines,
      status,
    };
    const call = isEditing
      ? supplyService.updatePurchaseOrder(poNumber, payload)
      : supplyService.savePurchaseOrder(payload);
    call
      .then(() => {
        setSaving(false);
        if (onSaved) onSaved();
        else onClose();
      })
      .catch((e) => {
        setSaving(false);
        setSaveErr(e?.response?.data?.message || e?.message || "Could not save the purchase order.");
      });
  };

  const subtitle = isEditing
    ? "Edit the order before it is sent to the supplier"
    : step === "source"
    ? "Choose how you want to build this purchase order"
    : PO_SOURCE_LABEL[source] || "Raise an order to a supplier · received later via a GRN";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          background: "var(--bg-canvas)",
          boxShadow: "-12px 0 40px rgba(13,17,27,0.18)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        className="cb-animate-in w-full sm:w-[min(1040px,96vw)]"
      >
        {/* Header bar */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            padding: "12px 20px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              background: "var(--ink-50)",
              color: "var(--ink-700)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconBook size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fg-strong)" }}>
              {isEditing ? `Edit ${editing.po.id}` : "New Purchase Order"}
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>{subtitle}</div>
          </div>
          {/* Only worth showing when there is more than one step left to walk. */}
          {step !== "source" && flow.length > 1 && <StepDots flow={flow} step={step} />}
          <button
            onClick={onClose}
            style={{
              border: 0,
              background: "transparent",
              cursor: "pointer",
              color: "var(--fg-muted)",
              width: 28,
              height: 28,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconX size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: "16px 20px" }} className="cb-scroll">
          {step === "source" ? (
            <PoSourceStep onPick={pickSource} />
          ) : step === "picker" ? (
            source === "out-of-stock" ? (
              <OutOfStockPoPicker warehouses={warehouses} onApply={applyPicked} />
            ) : (
              <PoPickerStep source={source} warehouses={warehouses} onApply={applyPicked} />
            )
          ) : (
            /* One page: PO details on top, products underneath. */
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <PoHeaderStep
                header={header}
                setH={setH}
                supplier={supplier}
                /* Only the selected store's suppliers are offered — see scopedSuppliers. */
                suppliers={scopedSuppliers}
                warehouses={warehouses}
              />
              <PoLineItemsStep lines={lines} setLines={setLines} totals={totals} />
            </div>
          )}
        </div>

        {step === "form" && !headerValid && (
          <div style={{ padding: "10px 20px 0" }}>
            <InlineAlert>
              PO details are incomplete — supplier, warehouse and PO date are required before saving.
            </InlineAlert>
          </div>
        )}

        {saveErr && (
          <div style={{ padding: "10px 20px 0" }}>
            <InlineAlert>{saveErr}</InlineAlert>
          </div>
        )}

        {/* Footer — the source + picker steps carry their own primary actions. */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            padding: "12px 20px",
            borderTop: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <Button onClick={onClose}>Cancel</Button>
          {canGoBack && <Button onClick={goBack}>← Back</Button>}
          <div style={{ flex: 1 }} />
          {step === "form" && (
            <>
              <span
                style={{
                  fontSize: 11,
                  color: canSave ? "var(--green-700)" : "var(--fg-muted)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {!headerValid
                  ? "Supplier + warehouse + date required"
                  : canSave
                  ? `${totals.items} item${totals.items !== 1 ? "s" : ""} · ${cbFmtINR(totals.amount)}`
                  : "Add at least one line item"}
              </span>
              <Button disabled={!canSave || saving} onClick={() => handleSubmit("DRAFT")}>
                {saving ? "Saving…" : "Save draft"}
              </Button>
              <Button
                variant="primary"
                icon={<IconCheck size={14} />}
                disabled={!canSave || saving}
                onClick={() => handleSubmit("APPROVED")}
              >
                {saving ? "Saving…" : "Create & approve"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const STEP_LABEL = { picker: "Select items", form: "PO details & products" };

const StepDots = ({ flow, step }) => {
  const steps = (flow || []).filter((id) => id !== "source");
  const curIdx = steps.indexOf(step);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
      {steps.map((id, i) => {
        const active = id === step;
        const done = curIdx > i;
        return (
          <React.Fragment key={id}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 99,
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: "var(--font-mono)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: done ? "var(--green-500)" : active ? "var(--ink-600)" : "var(--bg-sunken)",
                  color: done || active ? "#fff" : "var(--fg-muted)",
                }}
              >
                {done ? <IconCheck size={11} /> : i + 1}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: active ? 700 : 600,
                  color: done ? "var(--green-700)" : active ? "var(--ink-700)" : "var(--fg-muted)",
                }}
              >
                {STEP_LABEL[id]}
              </span>
            </span>
            {i < steps.length - 1 && <span style={{ width: 18, height: 2, background: "var(--bg-sunken)" }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ---------------- Step 0 · Source chooser ---------------- */
const PO_SOURCES = [
  {
    id: "manual",
    title: "Manual entry",
    desc: "Search the catalogue and build the order line by line.",
    icon: IconPlus,
    accentBg: "var(--ink-50)",
    accentFg: "var(--ink-700)",
  },
  {
    id: "low-stock",
    title: "From low stock",
    desc: "Pull in batches below a threshold and top them back up.",
    icon: IconReceipt,
    accentBg: "var(--amber-50, #fef3e2)",
    accentFg: "var(--amber-700)",
  },
  {
    id: "out-of-stock",
    title: "From out-of-stock orders",
    desc: "Reorder the medicines behind orders stuck on shortage.",
    icon: IconBook,
    accentBg: "var(--red-50, #fdecec)",
    accentFg: "var(--red-700)",
  },
];

const PoSourceStep = ({ onPick }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <Eyebrow style={{ display: "block" }}>Start a purchase order from…</Eyebrow>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
      {PO_SOURCES.map((s) => {
        const Icon = s.icon;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onPick(s.id)}
            className="cb-report-card"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 12,
              textAlign: "left",
              padding: 18,
              border: "1px solid var(--border-default)",
              borderRadius: 10,
              background: "var(--bg-panel)",
              cursor: "pointer",
              minHeight: 168,
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: s.accentBg,
                color: s.accentFg,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon size={20} />
            </span>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fg-strong)" }}>{s.title}</div>
            <div style={{ fontSize: 12.5, color: "var(--fg-muted)", lineHeight: 1.45, flex: 1 }}>{s.desc}</div>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
                fontWeight: 600,
                color: "var(--fg-link)",
              }}
            >
              Continue <IconArrowRight size={13} />
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

/* ---------------- Item picker (low-stock / out-of-stock) ----------------
   Fetches reorder candidates for the scoped warehouse straight from the APIs
   (inventory lots for low-stock, the out-of-stock report for stuck orders),
   lets the operator multi-select, then hands deduped seed lines to the drawer. */
/* Checkbox that can show the mixed (indeterminate) state for a parent order
   whose short lines are only partly selected. */
const TriCheckbox = ({ checked, indeterminate, onChange, ariaLabel }) => {
  const ref = React.useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !checked && !!indeterminate;
  }, [checked, indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel}
      onClick={(e) => e.stopPropagation()}
      style={{ cursor: "pointer" }}
    />
  );
};

/* "2026-07-16T10:58:32.7" -> "2026-07-16 10:58". */
const fmtStuckDate = (iso) => fmtDateTime(iso) || "—";
/* Reorder qty for a stuck line = the shortfall (fallback to ordered qty). */
const lineReorderQty = (it) => (Number(it.shortageQty) || 0) || (Number(it.quantity) || 0);
const stuckLineKey = (orderId, it, idx) => `${orderId}::${it.itemId ?? `${it.drugCode || "x"}#${idx}`}`;

/* Out-of-stock PO picker — mirrors the Out of Stuck report (warehouse, status,
   datetime range, sort, search, paging, expandable line detail) and adds
   hierarchical checkboxes: an order's checkbox toggles all of its short lines,
   each line can also be toggled on its own. Selection is a Map keyed by line
   (value = the PO seed item) so it survives paging and carries its reorder qty.
   Selected lines are merged by medicine (buildPoSeedLines) to seed the PO. */
const OutOfStockPoPicker = ({ warehouses, onApply }) => {
  /* The store being reordered for is the one in scope app-wide — the PO this seeds
     is raised for that store, so offering a different one here could only produce a
     PO for stock nobody is short of. */
  const { code: wh } = useWarehouse();
  const [filters, setFilters] = useState(() => ({ status: "ALL", ...last7DaysRange(), search: "", sort: "ASC" }));
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const [applied, setApplied] = useState(filters);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [pageState, setPageState] = useState({ page: 0, size: 20, totalPages: 0, totalElements: 0 });
  const setPageField = (k, v) => setPageState((s) => ({ ...s, [k]: v, ...(k === "size" ? { page: 0 } : {}) }));

  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (id) =>
    setExpanded((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  /* lineKey -> seed item { medId, productName, quantity }. */
  const [selected, setSelected] = useState(() => new Map());

  /* Debounce filter edits → applied (search would otherwise fetch per keystroke). */
  useEffect(() => {
    const t = setTimeout(() => setApplied(filters), 400);
    return () => clearTimeout(t);
  }, [filters]);

  /* New filter set / warehouse → back to the first page. */
  useEffect(() => {
    setPageState((s) => (s.page === 0 ? s : { ...s, page: 0 }));
  }, [applied, wh]);

  /* Selection is scoped to the loaded warehouse. */
  useEffect(() => {
    setSelected(new Map());
  }, [wh]);

  useEffect(() => {
    if (!wh) {
      setRows([]);
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    outOfStockService
      .list(
        { warehouseId: wh, status: applied.status, search: applied.search, sort: applied.sort, ...toDateTimeRange(applied) },
        { page: pageState.page, size: pageState.size }
      )
      .then((res) => {
        if (!alive) return;
        setRows(res.content || []);
        setPageState((s) => ({ ...s, totalPages: res.totalPages || 0, totalElements: res.totalElements || 0 }));
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load stuck orders.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, applied, pageState.page, pageState.size, reloadKey]);

  /* --- hierarchical selection --- */
  /* PO seed item for one stuck line, tagged with the allocation that traces it
     back to its order (buildPoSeedLines concatenates these when the same
     medicine spans several orders). */
  const lineSeed = (o, it) => {
    const qty = lineReorderQty(it);
    return {
      medId: it.drugCode,
      productName: it.drugName,
      quantity: qty,
      allocations: [
        {
          orderId: o.orderId,
          orderDetailsId: it.itemId != null ? String(it.itemId) : "",
          medicineCode: it.drugCode,
          quantity: qty,
        },
      ],
    };
  };

  const orderLineEntries = (o) =>
    (o.items || []).map((it, idx) => [stuckLineKey(o.id, it, idx), lineSeed(o, it)]);

  const toggleLine = (key, seed) =>
    setSelected((prev) => {
      const next = new Map(prev);
      next.has(key) ? next.delete(key) : next.set(key, seed);
      return next;
    });

  const orderState = (o) => {
    const entries = orderLineEntries(o);
    if (!entries.length) return { checked: false, indeterminate: false };
    const sel = entries.filter(([k]) => selected.has(k)).length;
    return { checked: sel === entries.length, indeterminate: sel > 0 && sel < entries.length };
  };
  const toggleOrder = (o) => {
    const entries = orderLineEntries(o);
    const allSel = entries.length > 0 && entries.every(([k]) => selected.has(k));
    setSelected((prev) => {
      const next = new Map(prev);
      entries.forEach(([k, seed]) => (allSel ? next.delete(k) : next.set(k, seed)));
      return next;
    });
  };

  const pageEntries = rows.flatMap(orderLineEntries);
  const pageAllSel = pageEntries.length > 0 && pageEntries.every(([k]) => selected.has(k));
  const pageSomeSel = pageEntries.some(([k]) => selected.has(k));
  const toggleAllPage = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      pageEntries.forEach(([k, seed]) => (pageAllSel ? next.delete(k) : next.set(k, seed)));
      return next;
    });

  const selCount = selected.size;
  const reorderUnits = Array.from(selected.values()).reduce((s, l) => s + (Number(l.quantity) || 0), 0);

  const confirm = () => {
    if (!selCount) return;
    onApply(buildPoSeedLines(Array.from(selected.values())), wh);
  };

  const whName = warehouses.find((w) => String(w.id) === String(wh))?.name || getWarehouseInfo()?.pharmacyName || wh;
  const start = pageState.totalElements === 0 ? 0 : pageState.page * pageState.size + 1;
  const end = Math.min(pageState.totalElements, (pageState.page + 1) * pageState.size);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {/* The store in scope, named for confirmation — chosen in the topbar. */}
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--fg-default)",
            padding: "6px 10px",
            border: "1px solid var(--border-default)",
            borderRadius: 6,
            whiteSpace: "nowrap",
          }}
        >
          {whName}
        </span>
        <Select value={filters.status} onChange={(e) => setF("status", e.target.value)}>
          <option value="ALL">All</option>
          <option value="CREATED">CREATED</option>
          <option value="CLOSED">CLOSED</option>
        </Select>
        <DateTimeRangeFilter
          label="Order date"
          from={filters.fromDate}
          to={filters.toDate}
          fromTime={filters.fromTime}
          toTime={filters.toTime}
          onApply={({ from, to, fromTime, toTime }) =>
            setFilters((f) => ({ ...f, fromDate: from, toDate: to, fromTime, toTime }))
          }
        />
        <Select value={filters.sort} onChange={(e) => setF("sort", e.target.value)}>
          <option value="ASC">Oldest first</option>
          <option value="DESC">Newest first</option>
        </Select>
        <Input
          placeholder="Order / patient / drug…"
          value={filters.search}
          onChange={(e) => setF("search", e.target.value)}
          style={{ width: 200, maxWidth: "100%" }}
        />
        <Button onClick={() => setFilters({ status: "ALL", ...last7DaysRange(), search: "", sort: "ASC" })}>Reset</Button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(pageState.totalElements)} stuck orders
        </div>
      </div>

      {/* Selection summary + primary CTA */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 12,
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid var(--border-default)",
          background: "var(--bg-subtle)",
        }}
      >
        <div style={{ display: "flex", gap: 18, flex: 1, flexWrap: "wrap" }}>
          <SummaryStat label="Lines selected" value={cbFmtNum(selCount)} />
          <SummaryStat label="Reorder units" value={cbFmtNum(reorderUnits)} accent="var(--green-700)" />
        </div>
        <Button variant="primary" icon={<IconArrowRight size={14} />} disabled={!selCount} onClick={confirm}>
          Use {selCount || ""} line{selCount !== 1 ? "s" : ""} · continue
        </Button>
      </div>

      {loading ? (
        <LoadingState label="Loading stuck orders…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          <Table
            columns={[
              {
                label: (
                  <TriCheckbox
                    checked={pageAllSel}
                    indeterminate={pageSomeSel && !pageAllSel}
                    onChange={toggleAllPage}
                    ariaLabel="Select all lines on this page"
                  />
                ),
                width: 32,
              },
              { label: "", width: 24 },
              { label: "Order ID" },
              { label: "Patient" },
              { label: "Channel" },
              { label: "Lines", align: "right" },
              { label: "Short", align: "right" },
              { label: "Created" },
              { label: "Status" },
            ]}
          >
            {rows.length === 0 && (
              <tr>
                <Td colSpan={9} style={{ textAlign: "center", color: "var(--fg-muted)", height: 72 }}>
                  No stuck orders match these filters.
                </Td>
              </tr>
            )}
            {rows.map((o) => {
              const isOpen = expanded.has(o.id);
              const st = orderState(o);
              return (
                <React.Fragment key={o.id}>
                  <Tr selected={isOpen} onClick={() => toggleExpand(o.id)}>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <TriCheckbox
                        checked={st.checked}
                        indeterminate={st.indeterminate}
                        onChange={() => toggleOrder(o)}
                        ariaLabel={`Select all lines of order ${o.orderId}`}
                      />
                    </Td>
                    <Td>
                      <IconChevRight
                        size={12}
                        style={{
                          transform: isOpen ? "rotate(90deg)" : "rotate(0)",
                          transition: "transform 140ms",
                          color: "var(--fg-muted)",
                        }}
                      />
                    </Td>
                    <Td mono style={{ fontWeight: 600 }}>
                      {o.orderId}
                    </Td>
                    <Td mono>{o.patientId || <span style={{ color: "var(--fg-muted)" }}>—</span>}</Td>
                    <Td>
                      <Pill variant="neutral">{o.sourceChannel || "—"}</Pill>
                    </Td>
                    <Td mono right>
                      {cbFmtNum((o.items || []).length)}
                    </Td>
                    <Td mono right style={{ fontWeight: 600, color: "var(--red-700)" }}>
                      {cbFmtNum(o.shortageQty)}
                    </Td>
                    <Td mono>{fmtStuckDate(o.createdAt)}</Td>
                    <Td>
                      <Pill variant={o.status === "CLOSED" ? "green" : "amber"} dot>
                        {o.status || "—"}
                      </Pill>
                    </Td>
                  </Tr>
                  {isOpen &&
                    ((o.items || []).length === 0 ? (
                      <Tr style={{ background: "var(--bg-sunken)" }}>
                        <Td />
                        <Td colSpan={8} style={{ padding: "8px 12px", fontSize: 12, color: "var(--fg-muted)" }}>
                          No line-item details for this order.
                        </Td>
                      </Tr>
                    ) : (
                      o.items.map((it, idx) => {
                        const key = stuckLineKey(o.id, it, idx);
                        const seed = lineSeed(o, it);
                        return (
                          <Tr key={key} style={{ background: "var(--bg-sunken)" }} onClick={() => toggleLine(key, seed)}>
                            <Td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selected.has(key)}
                                onChange={() => toggleLine(key, seed)}
                                aria-label={`Select ${it.drugName || it.drugCode}`}
                                style={{ cursor: "pointer" }}
                              />
                            </Td>
                            <Td />
                            <Td mono colSpan={2} style={{ color: "var(--fg-muted)" }}>
                              {it.drugCode}
                            </Td>
                            <Td colSpan={2} style={{ fontWeight: 600, color: "var(--fg-strong)" }}>
                              {it.drugName}
                            </Td>
                            <Td mono right style={{ color: "var(--fg-muted)" }}>
                              {cbFmtNum(it.quantity)}
                            </Td>
                            <Td mono right style={{ fontWeight: 600, color: "var(--red-700)" }}>
                              {cbFmtNum(lineReorderQty(it))}
                            </Td>
                            <Td />
                          </Tr>
                        );
                      })
                    ))}
                </React.Fragment>
              );
            })}
          </Table>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
            <Select value={pageState.size} onChange={(e) => setPageField("size", Number(e.target.value))}>
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Select>
            <div style={{ marginLeft: "auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {start}–{end} of {cbFmtNum(pageState.totalElements)}
              </span>
              <Button size="sm" disabled={pageState.page <= 0} onClick={() => setPageField("page", Math.max(0, pageState.page - 1))}>
                ← Prev
              </Button>
              <Button
                size="sm"
                disabled={pageState.page + 1 >= pageState.totalPages}
                onClick={() => setPageField("page", pageState.page + 1)}
              >
                Next →
              </Button>
            </div>
          </div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>
            Tick a whole order to select all its short lines, or tick individual lines. The same medicine across orders is
            merged into one PO line; add purchase prices below.
          </div>
        </>
      )}
    </div>
  );
};

const usePoCandidates = (source, wh) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!wh) {
      setRows([]);
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    const req =
      source === "low-stock"
        ? supplyService.getInventoryLots(wh, { page: 0, size: 2000 }).then((r) => r.content || [])
        : outOfStockService.list({ warehouseId: wh }, { page: 0, size: 200 }).then((r) => r.content || []);
    req
      .then((data) => alive && (setRows(data), setLoading(false)))
      .catch((e) => alive && (setError(e?.message || "Could not load reorder candidates."), setLoading(false)));
    return () => {
      alive = false;
    };
  }, [source, wh, reloadKey]);

  return { rows, loading, error, reload: () => setReloadKey((k) => k + 1) };
};

const PoPickerStep = ({ source, warehouses, onApply }) => {
  const isLow = source === "low-stock";
  /* Same store as the PO being raised — see OutOfStockPoPicker. */
  const { code: wh } = useWarehouse();
  const [threshold, setThreshold] = useState(100);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  const { rows, loading, error, reload } = usePoCandidates(source, wh);

  /* Selection is scoped to the loaded warehouse. */
  useEffect(() => {
    setSelected(new Set());
  }, [wh, source]);

  const q = query.trim().toLowerCase();
  const candidates = useMemo(() => {
    let list = rows;
    if (isLow) list = rows.filter((l) => l.qtyA < threshold).sort((a, b) => a.qtyA - b.qtyA);
    if (q) {
      list = list.filter((r) => {
        const hay = isLow
          ? [r.medicineName, r.medId, r.batch]
          : [r.orderId, r.patientId, ...(r.items || []).map((it) => it.drugName)];
        return hay.filter(Boolean).join(" ").toLowerCase().includes(q);
      });
    }
    return list;
  }, [rows, isLow, threshold, q]);

  const selRows = candidates.filter((r) => selected.has(r.id));
  const allSel = candidates.length > 0 && selRows.length === candidates.length;
  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAll = () => setSelected(allSel ? new Set() : new Set(candidates.map((r) => r.id)));

  const reorderUnits = isLow
    ? selRows.reduce((s, l) => s + Math.max(1, threshold - l.qtyA), 0)
    : selRows.reduce((s, o) => s + (o.items || []).reduce((t, it) => t + (Number(it.quantity) || 0), 0), 0);

  const confirm = () => {
    if (!selRows.length) return;
    const items = isLow
      ? selRows.map((l) => ({
          medId: l.medId,
          productName: l.medicineName,
          quantity: Math.max(1, threshold - l.qtyA),
          purchasePrice: l.purchase,
          mrp: l.mrp,
          salePrice: l.sale,
          taxPct: l.gstPct,
        }))
      : selRows.flatMap((o) => (o.items || []).map((it) => ({ medId: it.drugCode, productName: it.drugName, quantity: it.quantity })));
    onApply(buildPoSeedLines(items), wh);
  };

  const whName = warehouses.find((w) => String(w.id) === String(wh))?.name || getWarehouseInfo()?.pharmacyName || wh;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {/* The store in scope, named for confirmation — chosen in the topbar. */}
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--fg-default)",
            padding: "6px 10px",
            border: "1px solid var(--border-default)",
            borderRadius: 6,
            whiteSpace: "nowrap",
          }}
        >
          {whName}
        </span>
        {isLow && (
          <>
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Below</span>
            <Input
              type="number"
              value={threshold}
              onChange={(e) => setThreshold(Math.max(0, Number(e.target.value) || 0))}
              style={{ width: 90 }}
            />
            <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>units</span>
          </>
        )}
        <Input
          placeholder={isLow ? "Medicine / code / batch…" : "Order / patient / drug…"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 220, maxWidth: "100%" }}
        />
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(candidates.length)} candidate{candidates.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Selection summary + primary CTA */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 12,
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid var(--border-default)",
          background: "var(--bg-subtle)",
        }}
      >
        <div style={{ display: "flex", gap: 18, flex: 1, flexWrap: "wrap" }}>
          <SummaryStat label="Selected" value={cbFmtNum(selRows.length)} />
          <SummaryStat label="Reorder units" value={cbFmtNum(reorderUnits)} accent="var(--green-700)" />
        </div>
        <Button
          variant="primary"
          icon={<IconArrowRight size={14} />}
          disabled={!selRows.length}
          onClick={confirm}
        >
          Use {selRows.length || ""} item{selRows.length !== 1 ? "s" : ""} · continue
        </Button>
      </div>

      {loading ? (
        <LoadingState label="Loading reorder candidates…" />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : isLow ? (
        <Table
          columns={[
            {
              label: (
                <input type="checkbox" checked={allSel} onChange={toggleAll} aria-label="Select all" style={{ cursor: "pointer" }} />
              ),
              width: 32,
            },
            { label: "Medicine" },
            { label: "Code" },
            { label: "Batch" },
            { label: "Available", align: "right" },
            { label: "Reorder qty", align: "right" },
          ]}
        >
          {candidates.length === 0 && (
            <tr>
              <Td colSpan={6} style={{ textAlign: "center", color: "var(--fg-muted)", height: 72 }}>
                No batches below {threshold} units.
              </Td>
            </tr>
          )}
          {candidates.map((l) => (
            <Tr key={l.id} selected={selected.has(l.id)} onClick={() => toggle(l.id)}>
              <Td onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected.has(l.id)}
                  onChange={() => toggle(l.id)}
                  aria-label={`Select ${l.medicineName}`}
                  style={{ cursor: "pointer" }}
                />
              </Td>
              <Td style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{l.medicineName}</Td>
              <Td mono>{l.medId}</Td>
              <Td mono>{l.batch}</Td>
              <Td mono right style={{ color: "var(--amber-700)", fontWeight: 600 }}>
                {cbFmtNum(l.qtyA)}
              </Td>
              <Td mono right style={{ color: "var(--green-700)", fontWeight: 600 }}>
                {cbFmtNum(Math.max(1, threshold - l.qtyA))}
              </Td>
            </Tr>
          ))}
        </Table>
      ) : (
        <Table
          columns={[
            {
              label: (
                <input type="checkbox" checked={allSel} onChange={toggleAll} aria-label="Select all" style={{ cursor: "pointer" }} />
              ),
              width: 32,
            },
            { label: "Order ID" },
            { label: "Patient" },
            { label: "Items", align: "right" },
            { label: "Short", align: "right" },
            { label: "Status" },
          ]}
        >
          {candidates.length === 0 && (
            <tr>
              <Td colSpan={6} style={{ textAlign: "center", color: "var(--fg-muted)", height: 72 }}>
                No stuck orders for this warehouse.
              </Td>
            </tr>
          )}
          {candidates.map((o) => (
            <Tr key={o.id} selected={selected.has(o.id)} onClick={() => toggle(o.id)}>
              <Td onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected.has(o.id)}
                  onChange={() => toggle(o.id)}
                  aria-label={`Select order ${o.orderId}`}
                  style={{ cursor: "pointer" }}
                />
              </Td>
              <Td mono style={{ fontWeight: 600 }}>
                {o.orderId}
              </Td>
              <Td mono>{o.patientId || "—"}</Td>
              <Td mono right>
                {cbFmtNum((o.items || []).length)}
              </Td>
              <Td mono right style={{ color: "var(--red-700)", fontWeight: 600 }}>
                {cbFmtNum(o.shortageQty)}
              </Td>
              <Td>
                <Pill variant={o.status === "CLOSED" ? "green" : "amber"} dot>
                  {o.status || "—"}
                </Pill>
              </Td>
            </Tr>
          ))}
        </Table>
      )}
      <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>
        {isLow
          ? "Reorder qty tops each batch back up to the threshold; prices are prefilled from the lot and stay editable."
          : "Same medicine across orders is merged into one line; add purchase prices below."}
      </div>
    </div>
  );
};

const SummaryStat = ({ label, value, accent }) => (
  <div style={{ display: "flex", flexDirection: "column" }}>
    <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--fg-muted)" }}>
      {label}
    </span>
    <span style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--font-mono)", color: accent || "var(--fg-strong)" }}>
      {value}
    </span>
  </div>
);

/* ---------------- PO header ---------------- */
const Field = ({ label, children, className }) => (
  <div className={className}>
    <label style={fieldLabel}>{label}</label>
    {children}
  </div>
);

const PoHeaderStep = ({ header, setH, supplier, suppliers, warehouses }) => (
  <div
    style={{
      background: "var(--bg-panel)",
      border: "1px solid var(--border-default)",
      borderRadius: 6,
      padding: "12px 16px",
    }}
  >
    <Eyebrow style={{ display: "block", marginBottom: 8 }}>Purchase order details</Eyebrow>
    <div style={{ overflowX: "auto" }} className="cb-scroll">
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[repeat(4,minmax(150px,1fr))_minmax(200px,1.6fr)] lg:min-w-[900px]"
        style={{
          gap: 8,
        }}
      >
        <Field label="Warehouse / store">
          {/* The store in scope, shown as a label: a PO is raised for the store you
              are working in, and that is chosen once in the topbar. */}
          <WarehouseSelect
            value={header.store}
            onChange={(v) => setH("store", v)}
            warehouses={warehouses}
            noneLabel="— Select store —"
            disabled
            width="100%"
          />
        </Field>
        <Field label="Supplier">
          <select
            value={header.supplier}
            onChange={(e) => {
              const s = suppliers.find((x) => String(x.id) === e.target.value);
              setH("supplier", e.target.value);
              /* Payment terms are set once when the supplier is onboarded, so they
                 are not editable here — they just follow the chosen supplier. */
              if (s?.terms) setH("paymentTerms", s.terms);
            }}
            style={fieldInput}
          >
            <option value="">— Select supplier —</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="PO date">
          <input
            type="date"
            value={header.poDate}
            onChange={(e) => setH("poDate", e.target.value)}
            style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
          />
        </Field>
        <Field label="Expected delivery">
          <input
            type="date"
            value={header.expectedDate}
            onChange={(e) => setH("expectedDate", e.target.value)}
            style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
          />
        </Field>
        <Field label="Remarks" className="col-span-2 sm:col-span-1">
          <input
            value={header.remarks}
            onChange={(e) => setH("remarks", e.target.value)}
            placeholder="Optional notes…"
            style={fieldInput}
          />
        </Field>
      </div>
    </div>

    <div style={{ marginTop: 8, fontSize: 11, color: "var(--green-700)", fontFamily: "var(--font-mono)" }}>
      Supplier GST: {supplier?.gstin || "—"} · Payment terms {supplier?.terms || "—"} (from supplier onboarding)
    </div>
  </div>
);

/* ---------------- Line items ---------------- */
/* Typeahead for the product field — searches the ES medicine-name service
   after 3+ chars (debounced 300 ms). Mirrors Grn.jsx's MedicineSearchField. */
const MedicineSearchField = ({ value, onSelect, onClear }) => {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = React.useRef(null);

  useEffect(() => {
    setQ(value || "");
  }, [value]);

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 3) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      supplyService
        .searchMedicines(trimmed)
        .then((items) => {
          setResults(items || []);
          setLoading(false);
        })
        .catch(() => {
          setResults([]);
          setLoading(false);
        });
    }, 300);
    return () => clearTimeout(handle);
  }, [q]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (m) => {
    onSelect(m);
    setQ(m.medicineName);
    setOpen(false);
  };

  const showDropdown = open && q.trim().length >= 3;
  const showHint = open && q.trim().length > 0 && q.trim().length < 3;

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          if (!e.target.value.trim() && onClear) onClear();
        }}
        onFocus={() => setOpen(true)}
        placeholder="Type 3+ chars to search…"
        style={fieldInput}
      />
      {showHint && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 2,
            padding: "6px 10px",
            background: "var(--bg-panel)",
            border: "1px solid var(--border-default)",
            borderRadius: 4,
            fontSize: 11,
            color: "var(--fg-muted)",
            zIndex: 30,
          }}
        >
          Type at least 3 characters to search.
        </div>
      )}
      {showDropdown && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 2,
            background: "var(--bg-panel)",
            border: "1px solid var(--border-default)",
            borderRadius: 4,
            boxShadow: "0 8px 20px rgba(13,17,27,0.12)",
            maxHeight: 260,
            overflowY: "auto",
            zIndex: 30,
          }}
        >
          {loading && <div style={{ padding: "8px 10px", fontSize: 11, color: "var(--fg-muted)" }}>Searching…</div>}
          {!loading && results.length === 0 && (
            <div style={{ padding: "8px 10px", fontSize: 11, color: "var(--fg-muted)" }}>
              No medicines match “{q.trim()}”.
            </div>
          )}
          {results.map((m) => (
            <div
              key={m.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(m)}
              style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid var(--border-subtle)", fontSize: 12 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-subtle)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <div style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{m.medicineName}</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-muted)", marginTop: 2 }}>
                {m.id}
                {m.medicineRate ? ` · ₹${m.medicineRate}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* Warning confirmation popup for destructive, non-undoable actions inside the
   drawer. z-index sits above the drawer (60) so it is not clipped by it. */
const ConfirmDialog = ({ title, message, confirmLabel = "Confirm", onConfirm, onCancel }) => (
  <div style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div onClick={onCancel} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
    <div
      role="alertdialog"
      aria-modal="true"
      style={{
        position: "relative",
        width: "min(420px, 92vw)",
        background: "var(--bg-panel)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
        boxShadow: "0 12px 40px rgba(13,17,27,0.22)",
        padding: "18px 20px",
      }}
      className="cb-animate-in"
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            flexShrink: 0,
            background: "var(--amber-50, var(--bg-sunken))",
            color: "var(--amber-700, var(--red-700))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          !
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--fg-strong)" }}>{title}</div>
      </div>
      <div style={{ fontSize: 12, color: "var(--fg-muted)", lineHeight: 1.5 }}>{message}</div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  </div>
);

const PoLineItemsStep = ({ lines, setLines, totals }) => {
  const [entry, setEntry] = useState(blankEntry());
  const [editIdx, setEditIdx] = useState(null);
  const [confirmClearLines, setConfirmClearLines] = useState(false);
  const setE = (key, val) => setEntry((e) => ({ ...e, [key]: val }));

  /* Drop every line. Also leaves edit mode — the line being edited is gone, so
     keeping editIdx would make Update write to an index that no longer exists. */
  const clearLines = () => {
    setLines([]);
    setEntry(blankEntry());
    setEditIdx(null);
    setConfirmClearLines(false);
  };

  const onSelectMedicine = (med) => {
    if (!med) {
      setEntry((e) => ({ ...e, medId: "", productCode: "", productName: "", manufacturer: "" }));
      return;
    }
    const rate = Number(med.medicineRate) || "";
    setEntry((e) => ({
      ...e,
      medId: med.id,
      productCode: med.id,
      productName: med.medicineName,
      manufacturer: med.manufacturer || "",
      mrp: rate,
      salePrice: rate,
    }));
  };

  const entryValid = entry.medId && num(entry.quantity) > 0 && num(entry.purchasePrice) > 0;

  const commitEntry = () => {
    if (!entryValid) return;
    if (editIdx !== null) {
      setLines((prev) => prev.map((l, i) => (i === editIdx ? entry : l)));
      setEditIdx(null);
    } else {
      setLines((prev) => [...prev, entry]);
    }
    setEntry(blankEntry());
  };

  const editLine = (i) => {
    setEntry(lines[i]);
    setEditIdx(i);
  };
  const delLine = (i) => {
    setLines((prev) => prev.filter((_, idx) => idx !== i));
    if (editIdx === i) {
      setEditIdx(null);
      setEntry(blankEntry());
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Line items table */}
      <div style={{ background: "var(--bg-panel)", border: "1px solid var(--border-default)", borderRadius: 6, overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-subtle)",
          }}
        >
          <Eyebrow>Line items · {lines.length}</Eyebrow>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
              {cbFmtNum(totals.qty)} units · GST {cbFmtINR(totals.tax)} · Total {cbFmtINR(totals.amount)}
            </span>
            <button
              onClick={() => setConfirmClearLines(true)}
              disabled={lines.length === 0}
              style={{
                border: 0,
                background: "transparent",
                color: lines.length ? "var(--red-700)" : "var(--fg-subtle, var(--fg-muted))",
                fontSize: 11,
                fontWeight: 600,
                cursor: lines.length ? "pointer" : "not-allowed",
              }}
            >
              Reset all
            </button>
          </div>
        </div>

        {confirmClearLines && (
          <ConfirmDialog
            title={`Remove all ${lines.length} line${lines.length !== 1 ? "s" : ""}?`}
            message="Every product on this purchase order will be removed, including rows seeded from the low-stock / out-of-stock picker. The PO details above are kept. This cannot be undone."
            confirmLabel="Remove all lines"
            onConfirm={clearLines}
            onCancel={() => setConfirmClearLines(false)}
          />
        )}
        <div style={{ overflowX: "auto" }} className="cb-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 820 }}>
            <thead>
              <tr style={{ background: "var(--bg-subtle)" }}>
                {["Edit", "Del", "Product code", "Product name", "Manufacturer", "Qty", "Free", "Purch price", "MRP", "Disc %", "GST %", "Margin", "Amount"].map(
                  (h, i) => (
                    <th key={h} style={{ ...poTh, textAlign: i > 4 ? "right" : "left" }}>
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr>
                  <td colSpan={13} style={{ ...poTd, textAlign: "center", color: "var(--fg-muted)", padding: 20 }}>
                    No products yet — add the first line below.
                  </td>
                </tr>
              )}
              {lines.map((l, i) => {
                const c = poLineCalc(l);
                return (
                  <tr key={i} style={{ background: editIdx === i ? "var(--ink-50)" : "transparent" }}>
                    <td style={{ ...poTd, textAlign: "center" }}>
                      <button onClick={() => editLine(i)} title="Edit line" style={iconBtn("var(--ink-600)")}>
                        <IconReceipt size={13} />
                      </button>
                    </td>
                    <td style={{ ...poTd, textAlign: "center" }}>
                      <button onClick={() => delLine(i)} title="Delete line" style={iconBtn("var(--red-700)")}>
                        <IconX size={13} />
                      </button>
                    </td>
                    <td style={{ ...poTd, fontFamily: "var(--font-mono)" }}>{l.productCode}</td>
                    <td style={{ ...poTd, fontWeight: 600, color: "var(--fg-strong)" }}>{l.productName}</td>
                    <td style={{ ...poTd, color: "var(--fg-muted)" }}>{l.manufacturer || "—"}</td>
                    <td style={{ ...poTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>{c.qty}</td>
                    <td style={{ ...poTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>{num(l.free)}</td>
                    <td style={{ ...poTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>{cbFmtINR(c.pp)}</td>
                    <td style={{ ...poTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>{cbFmtINR(num(l.mrp))}</td>
                    <td style={{ ...poTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>{num(l.discountPct)}%</td>
                    <td style={{ ...poTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>{num(l.taxPct)}%</td>
                    <td
                      style={{
                        ...poTd,
                        textAlign: "right",
                        fontFamily: "var(--font-mono)",
                        color: c.margin >= 0 ? "var(--green-700)" : "var(--red-700)",
                        fontWeight: 600,
                      }}
                    >
                      {c.margin.toFixed(1)}%
                    </td>
                    <td style={{ ...poTd, textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 600 }}>
                      {cbFmtINR(c.amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product entry form */}
      <div style={{ background: "var(--bg-panel)", border: "1px solid var(--border-default)", borderRadius: 6, padding: "12px 16px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <Eyebrow>{editIdx !== null ? `Editing line ${editIdx + 1}` : "Add product"}</Eyebrow>
          {editIdx !== null && (
            <button
              onClick={() => {
                setEditIdx(null);
                setEntry(blankEntry());
              }}
              style={{ border: 0, background: "transparent", color: "var(--fg-link)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
            >
              Cancel edit
            </button>
          )}
        </div>

        <div
          className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-[1.8fr_1.3fr_0.7fr_0.7fr_1fr_1fr_0.8fr_0.8fr_auto]"
          style={{ gap: 10, alignItems: "end" }}
        >
          <Field label="Product name" className="col-span-2 lg:col-span-1">
            <MedicineSearchField value={entry.productName} onSelect={onSelectMedicine} onClear={() => onSelectMedicine(null)} />
          </Field>
          <Field label="Manufacturer">
            <input
              type="text"
              value={entry.manufacturer}
              onChange={(e) => setE("manufacturer", e.target.value)}
              placeholder="Auto-filled from product"
              style={fieldInput}
            />
          </Field>
          <Field label="Quantity">
            <input type="number" value={entry.quantity} onChange={(e) => setE("quantity", e.target.value)} style={fieldMono} />
          </Field>
          <Field label="Free qty">
            <input type="number" value={entry.free} onChange={(e) => setE("free", e.target.value)} style={fieldMono} />
          </Field>
          <Field label="Purchase price">
            <input type="number" value={entry.purchasePrice} onChange={(e) => setE("purchasePrice", e.target.value)} style={fieldMono} />
          </Field>
          <Field label="MRP">
            <input type="number" value={entry.mrp} onChange={(e) => setE("mrp", e.target.value)} style={fieldMono} />
          </Field>
          <Field label="Disc %">
            <input type="number" value={entry.discountPct} onChange={(e) => setE("discountPct", e.target.value)} style={fieldMono} />
          </Field>
          <Field label="GST %">
            <input type="number" value={entry.taxPct} onChange={(e) => setE("taxPct", e.target.value)} style={fieldMono} />
          </Field>
          <Button variant="primary" icon={<IconPlus size={14} />} onClick={commitEntry} disabled={!entryValid}>
            {editIdx !== null ? "Update" : "Add"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrder;
